import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, ListTree, Pencil, Plus, Trash2 } from 'lucide-react';
import clsx from 'clsx';
import type { Category, CategoryNode } from '@sebastian/shared';
import { api, ApiError } from '../lib/api';
import { useCategories, useCategoryTree } from '../hooks/queries';
import { useToast } from '../hooks/useToast';
import {
  Badge,
  Button,
  Card,
  ConfirmDialog,
  EmptyState,
  ErrorState,
  Field,
  Input,
  Modal,
  Select,
  Spinner,
  Textarea,
  Toggle,
} from '../components/ui';

interface Draft {
  id?: string;
  name: string;
  parentId: string;
  description: string;
  sortOrder: number;
  isActive: boolean;
}

const EMPTY: Draft = { name: '', parentId: '', description: '', sortOrder: 0, isActive: true };

/** Nested category management; the tree may be arbitrarily deep. */
export function CategoriesAdminPage() {
  const toast = useToast();
  const queryClient = useQueryClient();
  const { data: tree, isLoading, error, refetch } = useCategoryTree(true);
  const { data: flat = [] } = useCategories(true);

  const [draft, setDraft] = useState<Draft | null>(null);
  const [removing, setRemoving] = useState<Category | null>(null);
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: ['categories'] });
    void queryClient.invalidateQueries({ queryKey: ['menu-items'] });
  };
  const failed = (caught: unknown, fallback: string) =>
    toast.error(caught instanceof ApiError ? caught.message : fallback);

  const save = useMutation({
    mutationFn: (value: Draft) => {
      const body = {
        name: value.name.trim(),
        parentId: value.parentId || null,
        description: value.description.trim() || null,
        sortOrder: value.sortOrder,
        isActive: value.isActive,
      };
      return value.id ? api.categories.update(value.id, body) : api.categories.create(body);
    },
    onSuccess: () => {
      invalidate();
      setDraft(null);
      toast.success('Category saved.');
    },
    onError: (caught) => failed(caught, 'Could not save that category.'),
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.categories.remove(id),
    onSuccess: () => {
      invalidate();
      setRemoving(null);
      toast.success('Category deleted.');
    },
    onError: (caught) => failed(caught, 'Could not delete that category.'),
  });

  const move = useMutation({
    mutationFn: ({ id, sortOrder }: { id: string; sortOrder: number }) =>
      api.categories.reorder([{ id, sortOrder }]),
    onSuccess: invalidate,
    onError: (caught) => failed(caught, 'Could not reorder.'),
  });

  const renderNode = (node: CategoryNode, depth: number, siblings: CategoryNode[]) => {
    const hasChildren = node.children.length > 0;
    const isCollapsed = collapsed.has(node.id);
    const index = siblings.findIndex((candidate) => candidate.id === node.id);

    return (
      <div key={node.id}>
        <div
          className="flex items-center gap-2 border-b border-line px-3 py-2.5 transition hover:bg-hover"
          style={{ paddingLeft: 12 + depth * 22 }}
        >
          {hasChildren ? (
            <button
              type="button"
              aria-label={isCollapsed ? 'Expand' : 'Collapse'}
              onClick={() =>
                setCollapsed((current) => {
                  const next = new Set(current);
                  if (next.has(node.id)) next.delete(node.id);
                  else next.add(node.id);
                  return next;
                })
              }
              className="text-ink-faint transition hover:text-ink"
            >
              {isCollapsed ? <ChevronRight size={16} /> : <ChevronDown size={16} />}
            </button>
          ) : (
            <span className="w-4" />
          )}

          <span className={clsx('flex-1 text-sm', node.isActive ? 'text-ink' : 'text-ink-faint')}>
            {node.name}
            {node.description ? (
              <span className="ml-2 text-xs text-ink-faint">{node.description}</span>
            ) : null}
          </span>

          {node.itemCount > 0 ? (
            <span className="tnum text-xs text-ink-faint">{node.itemCount} items</span>
          ) : null}
          {!node.isActive ? <Badge tone="neutral">Hidden</Badge> : null}

          <div className="flex gap-1">
            <button
              type="button"
              aria-label="Move up"
              disabled={index === 0}
              onClick={() => move.mutate({ id: node.id, sortOrder: Math.max(0, node.sortOrder - 1) })}
              className="rounded p-1.5 text-ink-faint transition hover:text-ink disabled:opacity-30"
            >
              ↑
            </button>
            <button
              type="button"
              aria-label="Move down"
              disabled={index === siblings.length - 1}
              onClick={() => move.mutate({ id: node.id, sortOrder: node.sortOrder + 1 })}
              className="rounded p-1.5 text-ink-faint transition hover:text-ink disabled:opacity-30"
            >
              ↓
            </button>
            <button
              type="button"
              aria-label="Edit"
              onClick={() =>
                setDraft({
                  id: node.id,
                  name: node.name,
                  parentId: node.parentId ?? '',
                  description: node.description ?? '',
                  sortOrder: node.sortOrder,
                  isActive: node.isActive,
                })
              }
              className="rounded p-1.5 text-ink-faint transition hover:text-accent"
            >
              <Pencil size={15} />
            </button>
            <button
              type="button"
              aria-label="Delete"
              onClick={() => setRemoving(node)}
              className="rounded p-1.5 text-ink-faint transition hover:text-danger"
            >
              <Trash2 size={15} />
            </button>
          </div>
        </div>

        {hasChildren && !isCollapsed
          ? node.children.map((child) => renderNode(child, depth + 1, node.children))
          : null}
      </div>
    );
  };

  return (
    <div className="mx-auto max-w-4xl px-5 py-6">
      <header className="mb-6 flex items-end justify-between gap-4">
        <div>
          <h1 className="font-display text-3xl tracking-wide text-ink">Categories</h1>
          <p className="mt-1 text-sm text-ink-faint">
            Nest categories as deeply as the menu needs. Items always sit in a leaf.
          </p>
        </div>
        <Button variant="primary" icon={<Plus size={16} />} onClick={() => setDraft({ ...EMPTY })}>
          New category
        </Button>
      </header>

      {isLoading ? <Spinner /> : null}
      {error ? <ErrorState error={error} onRetry={() => void refetch()} /> : null}

      {tree && tree.length === 0 ? (
        <EmptyState icon={<ListTree size={30} />} title="No categories yet" message="Create the first one." />
      ) : null}

      {tree && tree.length > 0 ? (
        <Card padded={false} className="overflow-hidden">
          {tree.map((node) => renderNode(node, 0, tree))}
        </Card>
      ) : null}

      <Modal
        open={draft !== null}
        onClose={() => setDraft(null)}
        title={draft?.id ? 'Edit category' : 'New category'}
        footer={
          <>
            <Button variant="ghost" onClick={() => setDraft(null)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              loading={save.isPending}
              disabled={!draft?.name.trim()}
              onClick={() => draft && save.mutate(draft)}
            >
              Save
            </Button>
          </>
        }
      >
        {draft ? (
          <div className="space-y-4">
            <Field label="Name">
              <Input
                autoFocus
                value={draft.name}
                onChange={(event) => setDraft({ ...draft, name: event.target.value })}
                placeholder="Desserts"
              />
            </Field>
            <Field label="Parent category" hint="Leave empty for a top-level category.">
              <Select
                value={draft.parentId}
                onChange={(event) => setDraft({ ...draft, parentId: event.target.value })}
              >
                <option value="">— Top level —</option>
                {flat
                  .filter((candidate) => candidate.id !== draft.id)
                  .map((candidate) => (
                    <option key={candidate.id} value={candidate.id}>
                      {candidate.name}
                    </option>
                  ))}
              </Select>
            </Field>
            <Field label="Description (optional)">
              <Textarea
                rows={2}
                value={draft.description}
                onChange={(event) => setDraft({ ...draft, description: event.target.value })}
              />
            </Field>
            <div className="flex items-center justify-between">
              <Field label="Sort order" className="w-32">
                <Input
                  type="number"
                  value={draft.sortOrder}
                  onChange={(event) => setDraft({ ...draft, sortOrder: Number(event.target.value) })}
                />
              </Field>
              <Toggle
                checked={draft.isActive}
                onChange={(isActive) => setDraft({ ...draft, isActive })}
                label="Visible on the POS"
              />
            </div>
          </div>
        ) : null}
      </Modal>

      <ConfirmDialog
        open={removing !== null}
        title={`Delete "${removing?.name ?? ''}"?`}
        message="Categories that still hold items or sub-categories cannot be deleted — deactivate them instead."
        confirmLabel="Delete"
        loading={remove.isPending}
        onCancel={() => setRemoving(null)}
        onConfirm={() => removing && remove.mutate(removing.id)}
      />
    </div>
  );
}
